import { Capacitor } from '@capacitor/core';
import { BackgroundRunner } from '@capacitor/background-runner';
import { Preferences } from '@capacitor/preferences';
import type { TCargoTypeResults } from '~~/shared/interfaces/truck';

const CACHE_PREFIX = 'trucker-background-cache:';
const CACHE_INDEX_KEY = `${CACHE_PREFIX}index`;
const TRIPS_CACHE_KEY = `${CACHE_PREFIX}trips`;
const RUNNER_LABEL = 'com.sgmtrans.trucker.background';

const runnerCacheSyncPromises = new Map<string, Promise<void>>();

const getStorageValue = async (key: string) => {
	if (Capacitor.isNativePlatform()) {
		return (await Preferences.get({ key })).value;
	}
	return localStorage.getItem(key);
};

const setStorageValue = async (key: string, value: string) => {
	if (Capacitor.isNativePlatform()) {
		await Preferences.set({ key, value });
		return;
	}
	localStorage.setItem(key, value);
};

const readCacheIndex = async () => {
	try {
		const value = await getStorageValue(CACHE_INDEX_KEY);
		const parsed: unknown = value ? JSON.parse(value) : [];
		return Array.isArray(parsed) ? parsed.filter((key): key is string => typeof key === 'string') : [];
	} catch (error) {
		console.error('Failed to read trucker cache index:', error);
		return [];
	}
};

export const getTruckerTripsCacheKey = () => TRIPS_CACHE_KEY;

export const getTruckerTripDetailsCacheKey = (tripId: string) =>
	`${CACHE_PREFIX}trip-details:${encodeURIComponent(tripId)}`;

export const getTruckerCargosCacheKey = (tripId: string, cargoTypeResults: TCargoTypeResults) =>
	`${CACHE_PREFIX}cargos:${encodeURIComponent(tripId)}:${cargoTypeResults}`;

export const getTruckerCargoDetailsCacheKey = (tripId: string, cargoId: string) =>
	`${CACHE_PREFIX}details:${encodeURIComponent(tripId)}:${encodeURIComponent(cargoId)}`;

export const getTruckerSntReportCacheKey = (tripId: string, sntReport: 'Simple' | 'Detailed') =>
	`${CACHE_PREFIX}snt:${encodeURIComponent(tripId)}:${sntReport}`;

export const configureTruckerBackgroundRunner = async (baseURL: string, token: string | null | undefined) => {
	if (!Capacitor.isNativePlatform() || !token) return;
	if (!baseURL) {
		console.error('Failed to configure trucker background refresh: API base URL is empty.');
		return;
	}

	try {
		await BackgroundRunner.dispatchEvent({
			label: RUNNER_LABEL,
			event: 'configureTruckerRefresh',
			details: { baseURL, token }
		});
	} catch (error) {
		console.error('Failed to configure trucker background refresh:', error);
	}
};

const syncRunnerCache = async (key: string) => {
	if (!Capacitor.isNativePlatform()) return;
	const existingSyncPromise = runnerCacheSyncPromises.get(key);
	if (existingSyncPromise) return existingSyncPromise;

	const syncPromise = (async () => {
		try {
			const runnerCache = await BackgroundRunner.dispatchEvent<Record<string, string>>({
				label: RUNNER_LABEL,
				event: 'readTruckerCache',
				details: { keys: [key] }
			});
			const runnerValue = runnerCache?.[key];
			if (!runnerValue) return;

			const existingValue = await getStorageValue(key);
			let shouldReplace = !existingValue;
			if (existingValue) {
				const existingEntry: unknown = JSON.parse(existingValue);
				const incomingEntry: unknown = JSON.parse(runnerValue);
				const existingUpdatedAt =
					existingEntry && typeof existingEntry === 'object' && 'updatedAt' in existingEntry
						? Number(existingEntry.updatedAt)
						: 0;
				const incomingUpdatedAt =
					incomingEntry && typeof incomingEntry === 'object' && 'updatedAt' in incomingEntry
						? Number(incomingEntry.updatedAt)
						: 0;
				shouldReplace = incomingUpdatedAt > existingUpdatedAt;
			}
			if (!shouldReplace) return;

			await setStorageValue(key, runnerValue);
			const indexedKeys = await readCacheIndex();
			if (!indexedKeys.includes(key)) {
				await setStorageValue(CACHE_INDEX_KEY, JSON.stringify([...indexedKeys, key]));
			}
		} catch (error) {
			console.error('Failed to synchronize trucker background cache:', error);
		}
	});

	runnerCacheSyncPromises.set(key, syncPromise);
	try {
		await syncPromise;
	} finally {
		runnerCacheSyncPromises.delete(key);
	}
};

export const getTruckerBackgroundCache = async <T>(key: string): Promise<T | null> => {
	if (key !== CACHE_INDEX_KEY) await syncRunnerCache(key);
	try {
		const value = await getStorageValue(key);
		if (!value) return null;
		const entry: unknown = JSON.parse(value);
		return entry && typeof entry === 'object' && 'data' in entry
			? (entry.data as T)
			: (entry as T);
	} catch (error) {
		console.error('Failed to read cached trucker data:', error);
		return null;
	}
};

export const saveTruckerBackgroundCache = async (key: string, data: unknown) => {
	const indexedKeys = await readCacheIndex();
	if (!indexedKeys.includes(key)) {
		await setStorageValue(CACHE_INDEX_KEY, JSON.stringify([...indexedKeys, key]));
	}
	await setStorageValue(key, JSON.stringify({ data, updatedAt: Date.now() }));
};

export const clearTruckerBackgroundCache = async () => {
	if (Capacitor.isNativePlatform()) {
		try {
			await BackgroundRunner.dispatchEvent({
				label: RUNNER_LABEL,
				event: 'clearTruckerCache',
				details: {}
			});
		} catch (error) {
			console.error('Failed to clear trucker background runner cache:', error);
		}
	}

	const indexedKeys = await readCacheIndex();
	const keys = new Set([TRIPS_CACHE_KEY, ...indexedKeys]);

	for (const key of keys) {
		try {
			if (Capacitor.isNativePlatform()) {
				await Preferences.remove({ key });
			} else {
				localStorage.removeItem(key);
			}
		} catch (error) {
			console.error('Failed to clear cached trucker data:', error);
		}
	}

	try {
		if (Capacitor.isNativePlatform()) {
			await Preferences.remove({ key: CACHE_INDEX_KEY });
		} else {
			localStorage.removeItem(CACHE_INDEX_KEY);
		}
	} catch (error) {
		console.error('Failed to clear trucker cache index:', error);
	}
};
